<template>
  <div
    class="input__container"
    :class="styleAttrs.class ?? ''"
    :style="styleAttrs.style ?? ''"
  >
    <div
      class="input input-text input-autogrowarea"
      :class="{
        'input-autogrowarea--oneline': !growing,
        'has-error': !!error
      }"
      @click="focus($event)"
    >
      <div class="input__element input-autogrowarea__sizer">
        {{ value + " " }}
      </div>

      <input
        v-if="growing == false"
        :id="id"
        ref="inputElement"
        class="input__element input-autogrowarea__input"
        :class="[
          ...inputClass,
          { untouched: state.untouched, 'is-hiding-errors': hideErrors }
        ]"
        :type="type"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :required="required"
        :value="value"
        :autofocus="autofocus"
        :autocomplete="autocompleteOptions ? 'off' : autocomplete"
        :list="autocompleteOptions ? state.datalistId : undefined"
        :form="form"
        v-bind="functionalAttrs"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter"
        @keydown="onKeyDown"
      />
      <textarea
        v-else
        :id="id"
        ref="inputElement"
        rows="1"
        class="input__element input-autogrowarea__input"
        :class="{ untouched: state.untouched }"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :value="value ?? ''"
        :required="required"
        :autofocus="autofocus"
        :form="form"
        v-bind="functionalAttrs"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter"
        @keydown="onKeyDown"
      ></textarea>

      <div class="input__buttons" @click.prevent="">
        <button
          v-for="icon in icons"
          :key="icon"
          type="button"
          class="button-icon input__icon"
          @click="onIconClick($event, icon)"
        >
          <lucide-icon :name="icon" />
        </button>

        <slot name="buttons"></slot>
      </div>
    </div>

    <datalist v-if="autocompleteOptions" :id="state.datalistId">
      <option
        v-for="option in autocompleteOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label ?? option.value }}
      </option>
    </datalist>

    <div class="input__before">
      <slot name="label">
        <label v-if="label" class="label input__label" :for="id">
          {{ label }}
        </label>
      </slot>
      <slot name="top-right"></slot>
    </div>

    <div class="input__after">
      <slot name="after"></slot>
      <span
        v-if="
          (error != '' && error != null) || (state.validationError != '' && !hideErrors)
        "
        class="input__error message-error"
      >
        {{ error || state.validationError }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch, onMounted, nextTick } from "vue";
import { v4 as uuid } from "uuid";
import { closestString } from "@/helpers/utility/levenshtein.js";
import LucideIcon from "@core/ui/LucideIcon.vue";
import { useAttrsAndStyle } from "@core/composables/attrsAndStyle";
import { useI18n } from "vue-i18n";

const acceptedIcons = ["plus", "eye", "eye-off"] as const;
type IconName = (typeof acceptedIcons)[number];

defineOptions({
  name: "InputText",
  // we do not inherit all attributes directly,
  // but instead only assign class & style to the
  // main container and move all others to the input.
  inheritAttrs: false
});

const props = withDefaults(
  defineProps<{
    value: string | undefined;

    placeholder?: string;

    /**
     * If submit is not prevented, any form the input is
     * in will be submitted if user presses enter.
     */
    preventSubmit?: boolean;
    maxlength?: number;

    icons?: IconName[];
    label?: string;
    required?: boolean;
    /**
     * One of the standard HTML input types
     * Only use the types generating a text box.
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
     */
    type?: string;
    id?: string;

    hideErrors?: boolean;
    error?: string;
    autofocus?: boolean;

    autocomplete?: string;
    autocompleteOptions?: { value: string; label?: string }[];

    /**
     * Only allow a value of the autocomplete suggestion
     */
    requiresOption?: boolean;
    /**
     * ID of the form the input belongs to.
     */
    form?: string;
    inputClass?: string[];
    /**
     * Allow the input to grow over multiple lines
     */
    growing?: boolean;
  }>(),
  {
    placeholder: "",
    preventSubmit: false,
    maxlength: undefined,
    icons: () => [],
    label: undefined,
    required: false,
    type: "text",
    id: () => 'textinput-' + uuid(),
    hideErrors: false,
    error: "",
    autofocus: false,
    autocomplete: undefined,
    autocompleteOptions: undefined,
    requiresOption: false,
    form: undefined,
    inputClass: () => [],
    growing: false
  }
);

const emit = defineEmits<{
  (event: "focus"): void;
  (event: "blur"): void;
  (event: "update:value", value: string): void;
  (event: "enter", value: string): void;
  (event: "keydown", keyboardEvent: KeyboardEvent): void;
  (event: `click:${IconName}`, mouseEvent: MouseEvent): void;
}>();

const { functionalAttrs, styleAttrs } = useAttrsAndStyle();
const { t } = useI18n();

const inputElement = ref<HTMLInputElement>();

const state = reactive({
  focussed: false,
  untouched: true,
  validationError: "",
  datalistId: uuid()
});

watch(
  () => props.value,
  (val) => {
    if (inputElement.value == null) return;
    if (val === inputElement.value.value) return;
    inputElement.value.value = val ?? "";
  },
  { immediate: true }
);

onMounted(() => {
  if (props.autofocus) {
    if (document.activeElement != inputElement.value) {
      focus();
    }
  }
});

function focus(mouseEvent?: MouseEvent) {
  if (mouseEvent && mouseEvent.defaultPrevented) return;
  if (inputElement.value == null) {
    console.warn("input not ready to focus");
    return;
  }
  inputElement.value.focus();
}

function select() {
  if (inputElement.value == null) {
    console.warn("input not ready to select");
    return;
  }
  inputElement.value.select();
}

function blur() {
  if (inputElement.value == null) return;
  nextTick(() => {
    if (inputElement.value == null) return;
    inputElement.value.blur();
  });
}
function onFocus() {
  state.focussed = true;
  emit("focus");
}
function onBlur() {
  state.focussed = false;
  state.untouched = false;
  updateErrorMessage();
  emit("blur");
}
function onInput($event: Event) {
  updateErrorMessage();
  if ($event.target == null) return;
  const inputElement = $event.target as HTMLInputElement;
  emit("update:value", inputElement.value);
}
function onEnter(event: KeyboardEvent) {
  if (props.preventSubmit) {
    event.preventDefault();
  }
  emit("enter", inputElement.value?.value || "");
}
function onKeyDown(keyEvent: KeyboardEvent) {
  emit("keydown", keyEvent);
}

function onIconClick(mouseEvent: MouseEvent, iconName: IconName) {
  emit(`click:${iconName}`, mouseEvent);
}

function updateErrorMessage() {
  const field = inputElement.value;

  if (!field?.validity) return;

  field.setCustomValidity("");
  field.checkValidity();

  if (field?.validity?.valid === false) {
    if (field.validity.valueMissing) {
      state.validationError = t(
        "errors.fieldErrors.valueMissing.labeled",
        {
          label: props.label
        }
      );
    } else if (field.validity.tooShort) {
      state.validationError = t(
        "errors.fieldErrors.tooShort",
        {
          min: field.minLength
        }
      );
    } else if (field.validity.tooLong) {
      state.validationError = t(
        "errors.fieldErrors.tooLong",
        {
          max: field.maxLength
        }
      );
    } else {
      state.validationError = field.validationMessage; // this.$t("errors.fieldRequired");
    }
  } else {
    // Field is valid according to HTML rules.
    // Do any custom rule checking here:

    // Check if one of the options is selected:
    if (props.requiresOption && props.autocompleteOptions) {
      const value = field.value;
      const allOptions = props.autocompleteOptions.map((o) =>
        o.value.toLowerCase()
      );
      const correspondingOptionIndex = allOptions.findIndex(
        (option) => option == value.toLowerCase()
      );

      if (correspondingOptionIndex == -1) {
        const optionIndexBeginningWithValue = allOptions.findIndex((option) =>
          option.toLocaleLowerCase().startsWith(value.toLocaleLowerCase())
        );
        const { distance, index } = closestString(
          value.toLowerCase(),
          allOptions
        );
        const nextBestOptionIndex = optionIndexBeginningWithValue >= 0 ? optionIndexBeginningWithValue : index;

        let errorMessage = t("errors.notValidOption", { value });

        if (distance < 4) {
          errorMessage +=
            " " + t("messages.didYouMean", { value: props.autocompleteOptions[nextBestOptionIndex].value });
        }

        state.validationError = errorMessage;
        field.setCustomValidity(errorMessage);
        return;
      } else {
        field.value = props.autocompleteOptions[correspondingOptionIndex].value;
      }
    }

    field.setCustomValidity("");
    state.validationError = "";
  }
}

defineExpose({
  focus,
  select,
  blur,
  inputElement
});
</script>

<style lang="scss">
.input-text {
  max-height: 20rem;
  overflow-y: auto;
}
</style>
